import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, ShieldAlert, Loader2, Download, ArrowRight, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SEV = { critical: "#FF3B30", high: "#FF7A45", warning: "#FFD60A", info: "#4da3ff" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function HealthcareCheck() {
  const navigate = useNavigate();
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    const d = domain.trim();
    if (!d) return;
    setLoading(true);
    setData(null);
    try {
      const res = await api.post("/public/hipaa-scan", { domain: d });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check that website.");
    } finally { setLoading(false); }
  };

  const hc = data?.healthcare || {};
  const findings = hc.findings || [];
  const riskColor = hc.riskLevel === "high" ? SEV.critical : hc.riskLevel === "medium" ? SEV.warning : "#30D158";
  const pdfUrl = data ? `${BACKEND}/api/public/hipaa-report.pdf?domain=${encodeURIComponent(data.domain)}` : "#";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button data-testid="hc-home" onClick={() => navigate("/")} className="flex items-center gap-2.5">
            <img src="/brand-mark.png" alt="Link Website Pro" className="w-8 h-8 object-contain" />
            <span className="font-heading font-bold tracking-tight brand-gradient">Link Website Pro</span>
          </button>
          <button data-testid="hc-login" onClick={() => navigate("/login")} className="text-sm text-zinc-300 hover:text-white transition-colors">Sign in</button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[#30D158] text-xs font-bold uppercase tracking-[0.2em] mb-4">
            <Stethoscope size={15} /> For Medical, Dental, Therapy &amp; Rehab Practices
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" data-testid="hc-title">
            Is your practice's website a <span className="brand-gradient">HIPAA risk?</span>
          </h1>
          <p className="text-zinc-400 mt-5 text-lg">
            Tracking pixels and analytics on healthcare websites are a leading cause of HHS/OCR HIPAA
            settlements. Run a free check to see what your website is exposing — <b>no patient data is accessed.</b>
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-2 max-w-xl mx-auto">
            <input
              data-testid="hc-domain-input"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()}
              placeholder="yourpractice.com"
              className="flex-1 bg-[#141414] border border-white/15 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#30D158]/60"
            />
            <button data-testid="hc-scan-btn" onClick={run} disabled={loading}
              className="btn-brand text-white rounded-xl px-6 py-3.5 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Stethoscope size={18} />}
              {loading ? "Checking…" : "Check my website free"}
            </button>
          </div>
          <p className="text-zinc-600 text-xs mt-3 flex items-center justify-center gap-1.5"><Lock size={12} /> Public website scan only · No patient data · Results in ~30 seconds</p>
        </div>

        {data && (
          <div className="mt-12 bg-[#121212] border rounded-2xl p-6" style={{ borderColor: `${riskColor}40` }} data-testid="hc-results">
            <div className="flex items-center justify-between gap-4 flex-wrap border-b border-white/10 pb-5 mb-5">
              <div>
                <p className="text-zinc-500 text-xs uppercase tracking-wide">HIPAA website-risk score</p>
                <p className="font-heading text-2xl font-bold mt-1">{data.domain}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ color: riskColor, background: `${riskColor}1a` }}>{hc.riskLevel || "low"} risk</span>
                <span className="font-heading text-4xl font-extrabold tabular-nums" style={{ color: riskColor }} data-testid="hc-score">{hc.score ?? "—"}<span className="text-zinc-600 text-base">/100</span></span>
              </div>
            </div>

            {findings.length ? (
              <div className="space-y-2">
                {findings.map((f) => (
                  <div key={f.id} className="border border-white/10 rounded-lg p-3.5" data-testid={`hc-finding-${f.id}`} style={{ background: `${SEV[f.severity]}0a` }}>
                    <div className="flex items-center gap-2">
                      <ShieldAlert size={14} style={{ color: SEV[f.severity] }} />
                      <span className="text-white text-sm font-medium">{f.title}</span>
                      <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ color: SEV[f.severity], background: `${SEV[f.severity]}1a` }}>{f.severity}</span>
                    </div>
                    <p className="text-zinc-400 text-xs mt-1.5 leading-relaxed">{f.detail}</p>
                    <p className="text-[#32D74B] text-xs mt-1.5 leading-relaxed"><span className="font-semibold">Fix:</span> {f.remediation}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8"><CheckCircle2 size={36} className="text-[#32D74B] mx-auto mb-2" /><p className="text-white font-medium">No HIPAA website-risk signals detected. Nicely done.</p></div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a data-testid="hc-pdf-btn" href={pdfUrl} target="_blank" rel="noreferrer"
                className="flex-1 border border-white/15 text-white hover:bg-white/10 rounded-xl px-5 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors">
                <Download size={16} /> Download branded PDF report
              </a>
              <button data-testid="hc-signup-btn" onClick={() => navigate("/login")}
                className="flex-1 btn-brand text-white rounded-xl px-5 py-3 text-sm font-medium inline-flex items-center justify-center gap-2">
                Fix these &amp; monitor my site <ArrowRight size={16} />
              </button>
            </div>
            <p className="text-zinc-600 text-[11px] mt-4 text-center">A website-risk assessment based on public information — not legal advice or a HIPAA compliance certification.</p>
          </div>
        )}

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          {[
            { t: "Tracking-pixel leaks", d: "Meta & Google pixels that can send patient info to third parties." },
            { t: "Insecure forms & transport", d: "Missing HTTPS or forms that submit patient data in the clear." },
            { t: "Missing privacy notices", d: "No Notice of Privacy Practices or privacy policy on the site." },
          ].map((c) => (
            <div key={c.t} className="bg-[#121212] border border-white/10 rounded-xl p-5">
              <p className="text-white font-medium">{c.t}</p>
              <p className="text-zinc-500 text-sm mt-1">{c.d}</p>
            </div>
          ))}
        </div>
      </main>

      <footer className="border-t border-white/10 mt-10">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between text-xs text-zinc-600 flex-wrap gap-2">
          <span>© Link Website Pro · Website-risk guidance, not legal advice.</span>
          <a href="/" className="text-[#4da3ff]">Explore the full platform →</a>
        </div>
      </footer>
    </div>
  );
}
